import { useEffect, useRef, useState } from 'react';
import { canEnableCursorTrail } from '../utils/canEnableCursorTrail';

function CursorTrail() {
  const canvasRef = useRef(null);
  const [enabled] = useState(() => canEnableCursorTrail());

  useEffect(() => {
    if (!enabled) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d', { alpha: true });
    let particles = [];
    let raf = 0;
    let running = false;
    let idleTimer = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const stopLoop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      running = false;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life -= p.decay;
        p.vy -= 0.02;

        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }

        // Solid circles — cheaper than per-particle radial gradients
        ctx.globalAlpha = p.type === 'ember' ? p.life * 0.55 : p.life;
        ctx.fillStyle = '#f66300';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;

      if (particles.length > 120) particles = particles.slice(-120);

      if (particles.length === 0) {
        stopLoop();
        return;
      }

      raf = requestAnimationFrame(draw);
    };

    const ensureLoop = () => {
      if (!running) {
        running = true;
        raf = requestAnimationFrame(draw);
      }
    };

    const handleMove = (e) => {
      const x = e.clientX;
      const y = e.clientY;

      // 2 sparks + 1 ember (was 3 + 1)
      for (let i = 0; i < 2; i++) {
        particles.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 2,
          vy: (Math.random() - 0.5) * 2 - 1,
          life: 1,
          decay: 0.016 + Math.random() * 0.02,
          size: Math.random() * 3.5 + 1.5,
          type: 'spark',
        });
      }

      particles.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 0.5,
        vy: -Math.random() * 1.5 - 0.5,
        life: 1,
        decay: 0.012 + Math.random() * 0.01,
        size: Math.random() * 6 + 4,
        type: 'ember',
      });

      ensureLoop();
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        if (particles.length === 0) stopLoop();
      }, 400);
    };

    document.addEventListener('mousemove', handleMove, { passive: true });

    return () => {
      window.removeEventListener('resize', resize);
      document.removeEventListener('mousemove', handleMove);
      window.clearTimeout(idleTimer);
      stopLoop();
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 8,
      }}
    />
  );
}

export default CursorTrail;
